"use client";
import { useSocialLogin } from "@/api/AppApi/services/auth";
import { SocialDriverTypes } from "@/api/AppApi/services/auth/interfaces/IAuth";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import CustomButton, {
  BtnRadius,
  BtnSizes,
  BtnTypes,
} from "@/app/components/lib/CustomButton";
import SpinnerForBtn from "@/app/components/lib/loaders/SpinnerForBtn";
import { useRouter } from "@/app/components/lib/navigation/useRouter";
import { isSignedInAtom } from "@/hooks/authAtoms";
import {
  Auth,
  FacebookAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { useAtom } from "jotai";
import { storeAuthTokens } from "@/utils/functions/cookieOperations";
import firebaseAuth from "./firebase";
import SvgAuthApple from "./social-button-icons/SvgAuthApple";
import SvgAuthFacebook from "./social-button-icons/SvgAuthFacebook";
import SvgAuthGoogle from "./social-button-icons/SvgAuthGoogle";
import { useState, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import { PagesPaths } from "@/utils/functions/pages-paths";
import useEventTrackers from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/useEventTrackers";
import { IGTagUserData } from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/helpers/interfaces";
interface Props {
  driver: SocialDriverTypes;
  onError?: (msg: string, fromClick: boolean) => void;
}

const SocialButton: React.FC<Props> = ({ driver, onError }) => {
  const searchParams = useSearchParams();
  const [signingIn, setSigningIn] = useState(false);
  const [, setSignedIn] = useAtom(isSignedInAtom);
  const { userLoginEventTracher } = useEventTrackers();
  const router = useRouter();
  const companyData = useGetCompanyQuery();
  const firebaseConfig = companyData.data?.result.theme.firebase_config;
  const authInstance = useMemo(
    () => (firebaseConfig ? (firebaseAuth(firebaseConfig) as Auth) : null),
    [firebaseConfig],
  );
  const currentProvider =
    driver === SocialDriverTypes.google
      ? new GoogleAuthProvider()
      : driver === SocialDriverTypes.facebook
      ? new FacebookAuthProvider()
      : new OAuthProvider("apple.com");

  const CurrentIcon =
    driver === SocialDriverTypes.google
      ? SvgAuthGoogle
      : driver === SocialDriverTypes.facebook
      ? SvgAuthFacebook
      : SvgAuthApple;
  const isFromCart = searchParams.get("fromCart");
  const socialLogin = useSocialLogin({
    onSuccess: (res) => {
      const { user } = res?.result ?? {};
      storeAuthTokens(
        res.result?.access_token ?? "",
        res.result?.refresh_token ?? "",
      );
      setSignedIn(true);
      const userData: IGTagUserData = {
        name: user?.name,
        email: user?.email,
        phone: user?.mobile,
        user_id: user?.id,
      };
      userLoginEventTracher(true, { ...userData });
      router.replace(isFromCart ? PagesPaths.cart : `/`);
    },
    onError: (_err) => {
    },
  });
  const signInWithFirebase = async () => {
    try {
      setSigningIn(true);
      if (!authInstance) {
        setSigningIn(false);
        onError?.(
          "Social login is not available: missing Firebase configuration.",
          true,
        );
        return;
      }
      const result = await signInWithPopup(
        authInstance,
        currentProvider,
      );
      const user = result.user;
      socialLogin.mutate({ driver: driver, uid: user.uid });
      setSigningIn(false);
    } catch (err: any) {
      // console.error(err);
      setSigningIn(false);
      let msg = "An unexpected error occurred during social login.";
      if (err?.message?.includes("Firebase: Error (auth/invalid-api-key)")) {
        msg = "Invalid Firebase configuration. Please contact support.";
      } else if (err?.message?.includes("auth/unauthorized-domain")) {
        msg = `Domain not authorized (${window.location.hostname}). Please add it to Firebase Console > Authentication > Settings > Authorized domains.`;
      } else if (err?.message?.includes("auth/popup-blocked")) {
        msg =
          "Popup was blocked. Please allow popups for this site and try again.";
      } else if (err?.message) {
        msg = err.message;
      }
      onError?.(msg, true);
    }
  };

  const isLoading = socialLogin.isLoading || signingIn;
  return (
    <CustomButton
      type={BtnTypes.neutral}
      size={BtnSizes.large}
      radius={BtnRadius.small}
      onClick={signInWithFirebase}
      fullWidth
    >
      {isLoading ? <SpinnerForBtn dark /> : <CurrentIcon />}
    </CustomButton>
  );
};

export default SocialButton;
